import { useMemo } from 'react';
import { trpc } from '@/lib/trpc';
import { ComposedChart, Line, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from 'recharts';
import { Dumbbell, Utensils, TrendingUp, ShieldCheck, ChevronDown, ChevronUp, Zap, AlertTriangle, CheckCircle2, Activity } from 'lucide-react';
import { useState } from 'react';

// ─── Circular Gauge ────────────────────────────────────────────────────────────
function ReadinessGauge({ score, label, zone }: { score: number; label: string; zone: 'green' | 'amber' | 'red' }) {
  const radius = 80;
  const strokeWidth = 14;
  const cx = 110;
  const cy = 110;
  // Half-circle arc: from 180° to 0° (left to right)
  const startAngle = 180;
  const endAngle = 0;
  const totalAngle = 180;
  const sweepAngle = (score / 100) * totalAngle;

  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const arcX = (deg: number) => cx + radius * Math.cos(toRad(deg));
  const arcY = (deg: number) => cy + radius * Math.sin(toRad(deg));

  // Track arc (full half-circle)
  const trackD = `M ${arcX(180)} ${arcY(180)} A ${radius} ${radius} 0 0 1 ${arcX(0)} ${arcY(0)}`;
  // Fill arc
  const fillEndAngle = 180 - sweepAngle;
  const largeArc = sweepAngle > 90 ? 1 : 0;
  const fillD = score > 0
    ? `M ${arcX(180)} ${arcY(180)} A ${radius} ${radius} 0 ${largeArc} 1 ${arcX(fillEndAngle)} ${arcY(fillEndAngle)}`
    : '';

  const zoneColor = zone === 'green' ? '#22c55e' : zone === 'amber' ? '#f59e0b' : '#ef4444';
  const zoneBg = zone === 'green' ? 'text-green-400' : zone === 'amber' ? 'text-amber-400' : 'text-red-400';

  return (
    <div className="flex flex-col items-center">
      <svg width={220} height={130} viewBox="0 0 220 130" className="overflow-visible">
        {/* Track */}
        <path d={trackD} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={strokeWidth} strokeLinecap="round" />
        {/* Fill */}
        {fillD && (
          <path d={fillD} fill="none" stroke={zoneColor} strokeWidth={strokeWidth} strokeLinecap="round"
            style={{ filter: `drop-shadow(0 0 6px ${zoneColor}88)` }} />
        )}
        {/* Score number */}
        <text x={cx} y={cy - 4} textAnchor="middle" fontSize={42} fontWeight="700" fill="white" fontFamily="monospace">
          {score}
        </text>
        {/* /100 */}
        <text x={cx} y={cy + 18} textAnchor="middle" fontSize={13} fill="rgba(255,255,255,0.4)" fontFamily="monospace">
          / 100
        </text>
      </svg>
      <div className={`font-mono text-lg font-bold tracking-widest mt-1 ${zoneBg}`}>{label}</div>
    </div>
  );
}

// ─── Custom Tooltip for dual chart ────────────────────────────────────────────
function DivergenceTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-3 text-xs font-mono space-y-1">
      <div className="text-zinc-400">{label}</div>
      {payload.map((p: any) => (
        <div key={p.dataKey} style={{ color: p.color }}>
          {p.name}: {p.dataKey === 'volumeLbs' ? `${(p.value / 1000).toFixed(0)}K lbs` : p.value != null ? `${p.value}/4` : 'N/A'}
        </div>
      ))}
    </div>
  );
}

// ─── Collapsible card ─────────────────────────────────────────────────────────
function IntelCard({ icon, title, children, defaultOpen = true }: { icon: React.ReactNode; title: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-zinc-800/40 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-primary">{icon}</span>
          <span className="font-mono text-sm font-semibold text-zinc-200 tracking-wide">{title}</span>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-zinc-500" /> : <ChevronDown className="h-4 w-4 text-zinc-500" />}
      </button>
      {open && <div className="px-5 pb-5">{children}</div>}
    </div>
  );
}

// ─── Macro bar ────────────────────────────────────────────────────────────────
function MacroBar({ label, actual, target, unit }: { label: string; actual: number; target: number; unit: string }) {
  const pct = target > 0 ? Math.min(100, Math.round((actual / target) * 100)) : 0;
  const color = pct >= 90 ? '#22c55e' : pct >= 70 ? '#f59e0b' : '#ef4444';
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs font-mono text-zinc-400">
        <span>{label}</span>
        <span style={{ color }}>{actual}{unit} / {target}{unit} — {pct}%</span>
      </div>
      <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────
export default function RecoverTab() {
  const { data, isLoading } = trpc.telemetry.getReadinessData.useQuery();

  const chartData = useMemo(() => {
    if (!data?.volumeFeeling?.weeklyStats) return [];
    return data.volumeFeeling.weeklyStats.map((w) => ({
      label: w.label,
      volumeLbs: w.volumeLbs,
      avgFeeling: w.avgFeeling,
      shadowTarget: w.shadowTarget ?? null,
    }));
  }, [data?.volumeFeeling?.weeklyStats]);

  if (isLoading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-48 bg-zinc-800/50 rounded-xl" />
        <div className="h-36 bg-zinc-800/50 rounded-xl" />
        <div className="h-36 bg-zinc-800/50 rounded-xl" />
        <div className="h-36 bg-zinc-800/50 rounded-xl" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-zinc-500 font-mono text-sm">
        <Activity className="h-10 w-10 mb-3 opacity-30" />
        <p>Log your first session to unlock Readiness Intelligence.</p>
      </div>
    );
  }

  const { readinessScore, readinessLabel, readinessZone, actionItems, nutrition, volumeFeeling, plateauCard, contextualRecovery } = data;

  const shadowStatusColor = volumeFeeling.shadowStatus === 'ahead' ? 'text-amber-400' : volumeFeeling.shadowStatus === 'on_track' ? 'text-green-400' : 'text-red-400';
  const shadowStatusIcon = volumeFeeling.shadowStatus === 'ahead' ? <Zap className="h-4 w-4" /> : volumeFeeling.shadowStatus === 'on_track' ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />;

  return (
    <div className="space-y-5 pb-8">

      {/* ── Hero: Readiness Gauge ─────────────────────────────────────────── */}
      <div className="bg-zinc-900/80 border border-zinc-800 rounded-xl p-6 flex flex-col items-center">
        <p className="font-mono text-xs text-zinc-500 tracking-widest uppercase mb-4">READINESS SCORE</p>
        <ReadinessGauge score={readinessScore} label={readinessLabel} zone={readinessZone} />
        <p className="text-zinc-400 text-xs font-mono mt-4 text-center">
          Here's what moves this number.
        </p>
        <div className="mt-3 space-y-2 w-full max-w-sm">
          {actionItems.map((item, i) => (
            <div key={i} className="flex items-start gap-2 bg-zinc-800/60 rounded-lg px-3 py-2">
              <span className="text-primary mt-0.5 shrink-0">→</span>
              <span className="text-xs font-mono text-zinc-300">{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Card 1: Nutrition ─────────────────────────────────────────────── */}
      <IntelCard icon={<Utensils className="h-4 w-4" />} title="WHAT'S YOUR NUTRITION DATA SAYING?">
        {!nutrition.hasData ? (
          <div className="text-xs font-mono text-zinc-500 py-2">
            Log at least 3 days of nutrition in the Fuel tab to unlock this card.
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-3">
              <MacroBar label="PROTEIN" actual={nutrition.avgProtein7d} target={nutrition.targetProtein} unit="g" />
              <MacroBar label="CALORIES" actual={nutrition.avgCalories7d} target={nutrition.targetCalories} unit=" kcal" />
            </div>
            {nutrition.proteinGap > 10 ? (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-xs font-mono text-zinc-300 space-y-1">
                <p>Your protein averaged <span className="text-amber-400 font-bold">{nutrition.avgProtein7d}g</span> over the last 7 days. Your target is <span className="text-white font-bold">{nutrition.targetProtein}g</span>.</p>
                <p>That <span className="text-amber-400 font-bold">{nutrition.proteinGap}g</span> gap is likely slowing recovery between sessions.</p>
                <p className="text-zinc-400 mt-2">Hit your target for 3 consecutive days and your readiness score will reflect it.</p>
              </div>
            ) : (
              <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 text-xs font-mono text-green-300">
                Protein intake is on target. This is supporting your recovery.
              </div>
            )}
          </div>
        )}
      </IntelCard>

      {/* ── Card 2: Volume / Feeling Divergence ───────────────────────────── */}
      <IntelCard icon={<TrendingUp className="h-4 w-4" />} title="WHAT IS YOUR VOLUME DATA ACTUALLY SAYING?">
        <div className="space-y-4">
          {/* Shadow line status */}
          {volumeFeeling.shadowStatus && (
            <div className={`flex items-start gap-2 bg-zinc-800/60 rounded-lg px-3 py-2 ${shadowStatusColor}`}>
              {shadowStatusIcon}
              <span className="text-xs font-mono">{volumeFeeling.shadowNote}</span>
            </div>
          )}

          {/* Dual chart: volume bars + shadow line + feeling line */}
          {chartData.length > 0 && (
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 8, right: 40, left: 4, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: '#a1a1aa', fontFamily: 'monospace', fontWeight: 600 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    yAxisId="vol"
                    orientation="left"
                    ticks={[0, 75000, 150000, 225000, 300000]}
                    tickFormatter={(v) => v === 0 ? '0' : `${(v / 1000).toFixed(0)}K`}
                    tick={{ fontSize: 10, fill: '#71717a', fontFamily: 'monospace' }}
                    axisLine={false}
                    tickLine={false}
                    width={38}
                  />
                  <YAxis
                    yAxisId="feel"
                    orientation="right"
                    domain={[0, 4]}
                    ticks={[0, 1, 2, 3, 4]}
                    tick={{ fontSize: 10, fill: '#71717a', fontFamily: 'monospace' }}
                    axisLine={false}
                    tickLine={false}
                    width={20}
                  />
                  <Tooltip content={<DivergenceTooltip />} />
                  <Bar yAxisId="vol" dataKey="volumeLbs" name="Volume" fill="#22c55e" fillOpacity={0.5} radius={[3, 3, 0, 0]} />
                  {/* Shadow target dashed line */}
                  <Line
                    yAxisId="vol"
                    type="monotone"
                    dataKey="shadowTarget"
                    name="Shadow Target"
                    stroke="rgba(255,255,255,0.55)"
                    strokeWidth={1.5}
                    strokeDasharray="5 4"
                    dot={false}
                    connectNulls
                  />
                  {/* Feeling score line */}
                  <Line
                    yAxisId="feel"
                    type="monotone"
                    dataKey="avgFeeling"
                    name="Feeling"
                    stroke="#f59e0b"
                    strokeWidth={2}
                    dot={{ fill: '#f59e0b', r: 3 }}
                    connectNulls
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Divergence alert */}
          {volumeFeeling.hasDivergence && volumeFeeling.divergenceNote && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-xs font-mono text-zinc-300">
              {volumeFeeling.divergenceNote}
            </div>
          )}
          {!volumeFeeling.hasDivergence && (
            <p className="text-xs font-mono text-zinc-500">Volume and feeling scores are aligned. No divergence detected.</p>
          )}

          <div className="flex flex-wrap gap-4 text-xs font-mono text-zinc-500">
            <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded-sm bg-green-500/50 inline-block" /> Volume (bars)</span>
            <span className="flex items-center gap-1.5"><span className="w-5 border-t border-dashed border-zinc-400 inline-block" /> Shadow target</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-amber-400 inline-block" /> Feeling score</span>
          </div>
        </div>
      </IntelCard>

      {/* ── Card 3: Plateau vs Under-recovered ───────────────────────────── */}
      <IntelCard icon={<ShieldCheck className="h-4 w-4" />} title="IS THIS A PLATEAU OR ARE YOU UNDER-RECOVERED?">
        {!plateauCard.hasPlateaus ? (
          <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 text-xs font-mono text-green-300">
            No active plateaus detected. Keep executing the current block.
          </div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-2">
              {plateauCard.plateauExercises.map((p, i) => (
                <div key={i} className="flex items-center justify-between bg-zinc-800/60 rounded-lg px-3 py-2">
                  <span className="text-xs font-mono text-zinc-300">{p.exerciseName}</span>
                  <span className="text-xs font-mono text-amber-400">{p.stallWeeks}w stall</span>
                </div>
              ))}
            </div>
            {readinessScore < 60 ? (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-xs font-mono text-zinc-300">
                <p className="font-semibold text-amber-400 mb-1">Recovery-related plateau</p>
                <p>Your data suggests this plateau is recovery-related, not a true strength ceiling. Rest and nutrition first — then retest.</p>
              </div>
            ) : (
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 text-xs font-mono text-zinc-300">
                <p className="font-semibold text-blue-400 mb-1">True strength plateau</p>
                <p>Your recovery looks solid. This is a true strength plateau. Time to change the stimulus — try a rep range shift or a new variation.</p>
              </div>
            )}
          </div>
        )}
      </IntelCard>

      {/* ── Contextual Recovery Actions ───────────────────────────────────── */}
      <IntelCard icon={<Dumbbell className="h-4 w-4" />} title="TARGETED RECOVERY ACTIONS" defaultOpen={false}>
        {contextualRecovery.actions.length === 0 ? (
          <div className="text-xs font-mono text-zinc-500 py-2">
            {contextualRecovery.recentCategoryCount === 0
              ? 'No sessions logged in the last 72 hours. Log a workout to unlock targeted recovery recommendations.'
              : 'No specific recovery actions available for the muscles you trained recently.'}
          </div>
        ) : (
          <div className="space-y-4">
            {contextualRecovery.lastSessionDate && (
              <p className="text-xs font-mono text-zinc-500">
                Based on sessions logged in the last 72 hours. These movements will accelerate recovery for your next session.
              </p>
            )}
            {contextualRecovery.actions.map((action, i) => (
              <div key={i}>
                <p className="text-xs font-mono text-primary font-semibold mb-2 uppercase tracking-wide">{action.category}</p>
                <div className="space-y-2">
                  {action.stretches.map((s, j) => (
                    <div key={j} className="bg-zinc-800/60 rounded-lg px-3 py-2.5">
                      <p className="text-xs font-mono text-zinc-200 font-semibold">{s.name}</p>
                      <p className="text-xs font-mono text-zinc-500 mt-0.5">{s.instruction}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </IntelCard>

    </div>
  );
}
